import { h } from 'preact'

// Two states, per the design:
//
//   collapsed  a navy circle with the chat glyph, the resting state
//   expanded   the same circle, with a cream pill beside it carrying the
//              invitation and a gold chevron
//
// The expansion is a width transition on one button, not a second element
// appearing. The circle stays in exactly the same place throughout, so nothing
// jumps under a thumb already reaching for it, and there is only ever one tap
// target with one accessible name.
//
// The cream pill is the button's own background. Collapsed, the navy circle
// covers it completely, so there is nothing to fade in or out.
//
// `bottomOffset` is set by App from the height of whatever fixed bar the page
// has at the bottom. On a mobile property page that is the booking drawer,
// which claims the same corner at the same z-index.
export default function Launcher({ onClick, expanded = false, bottomOffset = 0 }) {
  return (
    <button
      onClick={onClick}
      class={`kailani-launcher fixed z-50 right-4 sm:right-6 flex items-center rounded-full
              shadow-lg bg-[#f5f0e8] text-left
              hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-[#c9a961]/40
              ${expanded ? 'kailani-launcher--expanded' : ''}`}
      style={`bottom: calc(1rem + env(safe-area-inset-bottom, 0px) + ${bottomOffset}px)`}
      aria-label="Chat with Kailani, your vacation planner"
    >
      {/* Fixed-size navy circle. Never shrinks, so the glyph does not drift. */}
      <span class="relative flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center bg-gradient-to-br from-[#1e3a8a] to-[#2d4ea0]">
        <svg class="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
        </svg>
        {/* Gold dot, the only thing drawing the eye while collapsed. */}
        <span class="kailani-launcher__dot absolute w-3 h-3 rounded-full bg-[#c9a961] top-1 right-1" />
      </span>

      {/* Always in the DOM so the button keeps one stable accessible name;
          clipped to zero width when collapsed. aria-hidden because the
          button's own aria-label already says this. */}
      <span class="kailani-launcher__label overflow-hidden whitespace-nowrap" aria-hidden="true">
        <span class="flex items-center gap-2 pl-3 pr-4">
          <span class="kailani-launcher__text text-[#1e3a8a] leading-tight">
            Design your vacation with me.
          </span>
          <svg class="w-4 h-4 flex-shrink-0 text-[#c9a961]" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="m9 5 7 7-7 7" />
          </svg>
        </span>
      </span>
    </button>
  )
}
